<template>
	<v-card :class="['indicator-panel', isCurrentLocalGerman ? 'lang-de' : 'lang-en']">
		<div class="header">
			<span class="title">{{ $t('set_indicators') }}</span>
			<button class="close-btn" @click="$emit('close')">✕</button>
		</div>

		<div class="card-body">
			<label class="row select-all text-caption">
				<div>
					<input type="checkbox" class="checkbox-all mr-2" :checked="allSelected" :indeterminate="someSelected" @change="toggleSelectAll" />
					<span>{{ $t('action_select_all') }}</span>
				</div>

				<span class="count"> {{ $t('selection_count', { selected: selectedCount, total: totalCount }) }} </span>
			</label>

			<div v-for="group in groups" :key="group.key" class="group">
				<div class="group-header" @click="toggleGroup(group.key)">
					<label class="row">
						<input
							type="checkbox"
							:checked="isGroupSelected(group)"
							:indeterminate="isGroupIndeterminate(group)"
							@click.stop
							@change="toggleGroupSelection(group)"
						/>
						<span class="group-title">{{ group.label }}</span>
					</label>

					<span class="chevron">
						<v-icon v-if="expandedGroups[group.key]">mdi-chevron-down</v-icon>
						<v-icon v-else>mdi-chevron-right</v-icon>
					</span>
				</div>

				<div v-if="expandedGroups[group.key]" class="group-items">
					<label v-for="item in group.items" :key="item.key" class="row item">
						<span class="indicator-icon">
							<v-icon v-if="item.icon" size="14" :class="item.key">
								{{ item.icon }}
							</v-icon>

							<img v-else-if="item.indicatorClass === 'dot-not-met'" :src="prefNotMetSVG" alt="" />

							<img v-else-if="item.indicatorClass === 'dot-pending-high'" :src="absencePendingHighSVG" alt="" />

							<img v-else-if="item.indicatorClass === 'dot-pending-low'" :src="absencePendingLowSVG" alt="" />

							<img v-else-if="item.indicatorClass === 'dot-preferred_workday_pending'" :src="preferredWorkDayDottedSVG" alt="" />

							<img v-else-if="item.indicatorClass === 'dot-preferred_workday_approved'" :src="preferredWorkDaySVG" alt="" />

							<span v-else class="dot" :class="item.indicatorClass" />
						</span>

						<input type="checkbox" v-model="selected[item.key]" />

						<div class="item-label">
							{{ item.label }}
						</div>
					</label>
				</div>
			</div>
		</div>

		<div class="footer">
			<button class="btn reset" @click="reset">
				{{ $t('core_dashboard_reset') }}
			</button>

			<button class="btn apply" @click="apply">
				{{ $t('action_apply') }}
			</button>
		</div>
	</v-card>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';

import prefNotMetSVG from '@/assets/Subtract_blue.svg';
import absencePendingHighSVG from '@/assets/Subtract_orange.svg';
import absencePendingLowSVG from '@/assets/Subtract_yellow.svg';
import preferredWorkDayDottedSVG from '@/assets/preferred_workday-dotted.svg';
import preferredWorkDaySVG from '@/assets/prefreed_workday.svg';

import { IndicatorGroup } from '../../types/components/types';
import { IndicatorOption } from '../../types/state/planning-view-state';

import { usePlanningStore } from '@/rennovation/planning-view/store/usePlanningStore';

const { locale } = useI18n();

const props = defineProps<{
	groups: IndicatorGroup[];
}>();

const emit = defineEmits<{
	(e: 'apply', value: IndicatorOption[]): void;
	(e: 'close'): void;
}>();

const planningStore = usePlanningStore();

const selected = ref<Record<string, boolean>>({});
const expandedGroups = ref<Record<string, boolean>>({});

const initializeState = () => {
	const newSelected: Record<string, boolean> = {};
	const newExpandedGroups: Record<string, boolean> = {};

	props.groups.forEach((group) => {
		newExpandedGroups[group.key] = true;

		group.items.forEach((item) => {
			const isSelected = planningStore.filters.selectedIndicators
				? planningStore.filters.selectedIndicators.some((selectedItem: any) => selectedItem.itemKey === item.key)
				: true;

			newSelected[item.key] = isSelected;
		});
	});

	selected.value = newSelected;
	expandedGroups.value = newExpandedGroups;
};

const toggleGroup = (groupKey: string) => {
	expandedGroups.value[groupKey] = !expandedGroups.value[groupKey];
};

const isGroupSelected = (group: IndicatorGroup) => {
	return group.items.every((item) => selected.value[item.key]);
};

const isGroupIndeterminate = (group: IndicatorGroup) => {
	const count = group.items.filter((item) => selected.value[item.key]).length;

	return count > 0 && count < group.items.length;
};

const toggleGroupSelection = (group: IndicatorGroup) => {
	const shouldSelect = !isGroupSelected(group);

	group.items.forEach((item) => {
		selected.value[item.key] = shouldSelect;
	});
};

const toggleSelectAll = () => {
	const value = !allSelected.value;

	Object.keys(selected.value).forEach((key) => {
		selected.value[key] = value;
	});
};

const reset = () => {
	Object.keys(selected.value).forEach((key) => {
		selected.value[key] = false;
	});
};

const apply = () => {
	const result = props.groups.flatMap((group) =>
		group.items
			.filter((item) => selected.value[item.key])
			.map((item) => ({
				itemKey: item.key,
				groupKey: group.key,
			}))
	);

	emit('apply', result);
	emit('close');
};

const totalCount = computed(() => Object.keys(selected.value).length);

const selectedCount = computed(() => Object.values(selected.value).filter(Boolean).length);

const allSelected = computed(() => totalCount.value > 0 && selectedCount.value === totalCount.value);

const someSelected = computed(() => selectedCount.value > 0 && !allSelected.value);

const isCurrentLocalGerman = computed(() => locale.value === 'de-CH');

onBeforeMount(() => {
	initializeState();
});
</script>

<style scoped lang="scss">
.indicator-panel {
	--violation-approved: #f9d2d3;
	--violation-unapproved: #e55f64;
	--violation-law: #9a191e;
	background: #fff;
	border: 1px solid #eee;
	display: flex;
	flex-direction: column;
	overflow: hidden;

	&.lang-de {
		width: 320px;
	}
	&.lang-en {
		width: 259px;
	}
}

.header {
	display: flex;
	justify-content: space-between;
	padding: 12px 4px;
	border-bottom: 1px solid #eee;
}

.title {
	font-weight: 600;
}

.close-btn {
	background: none;
	border: none;
	cursor: pointer;
}

.row {
	display: flex;
	align-items: center;
	gap: 8px;
}

.select-all {
	padding: 10px 4px;
	justify-content: space-between;
}

.count {
	font-size: 12px;
	color: #666;
}

.group {
	border-top: 1px solid #f0f0f0;
}

.group-header {
	padding: 8px 4px;
	display: flex;
	justify-content: space-between;
	cursor: pointer;
}

.group-items {
	padding-left: 20px;
}

.item {
	padding: 8px 0;
}

.chevron {
	font-size: 12px;
}

input[type='checkbox'] {
	accent-color: #0f766e !important;
}

.footer {
	margin-top: auto;
	padding-top: 12px;
	display: flex;
	justify-content: end;
	border-top: 1px solid #eee;
	background-color: white;
}

.btn {
	padding: 6px 14px;
	border-radius: 6px;
	font-weight: 500;
	text-transform: uppercase;
}

.reset {
	background: transparent;
	border: none;
	color: #555;
	margin-right: 10px;
}

.apply {
	background: #0f766e;
	border: none;
	color: white;
}

.dot {
	width: 14px;
	height: 14px;
	border-radius: 50%;
}

.dot-approved {
	background: var(--violation-approved);
}

.dot-unapproved {
	background: var(--violation-unapproved);
}

.dot-unapproved-law {
	background: var(--violation-law);
}

.dot-met {
	background: #4f8df7;
}

.dot-partial {
	background: #c7d2fe;
}

.dot-approved-high {
	background: #f97316;
}

.dot-approved-low {
	background: #fdba74;
}

.row.item {
	display: flex;
	align-items: flex-start;
	gap: 8px;
}

.row.item input[type='checkbox'] {
	margin-top: 4px;
}

.indicator-icon {
	width: 14px;
	min-width: 14px;
	height: 14px;
	min-height: 14px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 3px;
}

.indicator-icon img,
.indicator-icon .dot {
	width: 14px;
	height: 14px;
	display: block;
}

.custom-icon {
	color: #757575;
}
.internalNotes {
	color: #ffd858;
}

.modifiedDuty {
	color: #7ceefd;
}

.item-label {
	font-size: 16px;
	font-weight: 400;
	white-space: normal;
	overflow-wrap: break-word;
}

.card-body {
	flex: 1 1 auto;
	overflow-y: auto;
	min-height: 0;
}
</style>
